/**
 * GoogleOneTapButton - Google One Tap Integration
 *
 * Follows legacy pattern from:
 * - BrowserButton.js (hidden button pattern)
 * - Uses Google GSI library (accounts.google.com/gsi/client)
 * - Receives JWT credential directly (simpler than OAuth code flow)
 *
 * Pattern:
 * 1. Load Google GSI script dynamically
 * 2. Initialize with google.accounts.id.initialize()
 * 3. Render hidden button with google.accounts.id.renderButton()
 * 4. Custom button triggers hidden Google button
 * 5. Receive JWT credential via callback
 */

import { useEffect, useRef } from 'react'
import { SocialButton } from '../../components'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            use_fedcm_for_button?: boolean
            use_fedcm_for_prompt?: boolean
            button_auto_select?: boolean
          }) => void
          renderButton: (
            element: HTMLElement,
            config: {
              type: string
              theme: string
              size: string
              text: string
              shape: string
              logo_alignment: string
            }
          ) => void
        }
      }
    }
  }
}

interface GoogleOneTapButtonProps {
  clientId: string
  onCredentialReceived: (credential: string) => void
  disabled?: boolean
  signIn?: boolean
}

/**
 * Google One Tap Button Component
 * Matches legacy BrowserButton.js implementation
 */
export function GoogleOneTapButton({
  clientId,
  onCredentialReceived,
  disabled = false,
  signIn = false,
}: GoogleOneTapButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null)

  // Handle click on custom button - triggers hidden Google button
  const handleClick = () => {
    if (disabled) return

    // Find the hidden Google button (matches legacy selector logic)
    const chromeElement = document.querySelector<HTMLDivElement>(
      '.g_id_signin div[role="button"]'
    )
    const otherElement = document.querySelector<HTMLDivElement>(
      '.g_id_signin div[id$="-overlay"]'
    )

    if (otherElement) {
      otherElement.click()
    } else if (chromeElement) {
      chromeElement.click()
    }
  }

  useEffect(() => {
    if (!clientId) return

    const loadScript = () => {
      // Check if script already loaded
      if (window.google?.accounts?.id) {
        initializeGoogle()
        return
      }

      // Load Google GSI script
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = initializeGoogle
      document.body.appendChild(script)
    }

    const initializeGoogle = () => {
      if (!window.google?.accounts?.id) {
        console.error('[GoogleOneTap] Google GSI library not loaded')
        return
      }

      // Initialize Google One Tap
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          onCredentialReceived(response.credential)
        },
        use_fedcm_for_button: true,
        use_fedcm_for_prompt: true,
        button_auto_select: false,
      })

      // Render hidden button
      if (buttonRef.current) {
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: signIn ? 'signin_with' : 'signup_with',
          shape: 'rectangular',
          logo_alignment: 'center',
        })
      }
    }

    loadScript()
  }, [clientId, onCredentialReceived, signIn])

  if (!clientId) return null

  return (
    <>
      {/* Hidden Google button (rendered by GSI library) */}
      <div ref={buttonRef} className="g_id_signin hidden" />

      {/* Custom styled button that triggers hidden button */}
      <SocialButton
        provider="google"
        onClick={handleClick}
        disabled={disabled}
        size="lg"
      />
    </>
  )
}
